import { gql, useLazyQuery } from '@apollo/client';
import type { OAuthError } from '@auth0/auth0-react';
import { useAuth0 } from '@auth0/auth0-react';
import styled from '@emotion/styled';
import type {
  HomePageGetForkliftSessionQuery,
  HomePageGetForkliftSessionQueryVariables,
  HomePageGetLoadingEventParentShipmentQuery,
  HomePageGetLoadingEventParentShipmentQueryVariables,
  HomePageGetPalletEventParentShipmentQuery,
  HomePageGetPalletEventParentShipmentQueryVariables,
} from 'generated/graphql';
import Head from 'next/head';
import { useCallback, useEffect, useState } from 'react';

import OpsTaskBacklogContainer from '@containers/admin/ops-task-backlog-container';
import { AdminLayout } from '@layouts/admin';
import CopyAll from '@mui/icons-material/CopyAll';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import { useRouter } from 'next/router';
import ItemLookup from './item-lookup';

export const GetMetabaseExceptionDashboardUrl = (
  exceptionId: string | string[],
) =>
  `https://metabase.kargo.zone/dashboard/18-exception-debugger?exception_external_id=${exceptionId}`;

export const GetTaskUrl = (taskId: string) => {
  const base = `${window.origin}`;
  return `${base}/ops_tasks/${taskId}?mode=View`;
};

export const ExternalIdFromUrl = (urlOrExternalId: string): string => {
  if (!urlOrExternalId.includes('cloudfront')) {
    return urlOrExternalId;
  }
  const url = new URL(urlOrExternalId);
  const pathParts = url.pathname.split('/');
  const file = (pathParts[2] || '').split('.');
  return file[0] || '';
};

const GetMediaUrl = (externalId: string, typeOfMedia: 'image' | 'video') => {
  const base = `${window.origin}`;
  return `${base}/media/${typeOfMedia}/${externalId}`;
};

const StyledContainer = styled.div`
  margin-top: 4px;
  margin-bottom: 4px;
  padding: 4px;
`;

const StyledLoadingAndErrorStateContainer = styled.div`
  min-height: ${({ theme }) => theme.sizing.scale2000};
`;

const HOMEPAGE_GET_LOADING_EVENT_PARENT_SHIPMENT = gql`
  query HomePageGetLoadingEventParentShipment($loadingEventId: Int!) {
    ops {
      loadingEvent(loadingEventId: $loadingEventId) {
        id
        shipmentId
      }
    }
  }
`;

const HOMEPAGE_GET_FORKLIFT_SESSION = gql`
  query HomePageGetForkliftSession($forkliftSessionId: ID!) {
    ops {
      forkliftSession(id: $forkliftSessionId) {
        id
      }
    }
  }
`;

const HOMEPAGE_GET_PALLET_EVENT_PARENT_SHIPMENT = gql`
  query HomePageGetPalletEventParentShipment($palletEventId: Int!) {
    ops {
      palletEvent(palletEventId: $palletEventId) {
        id
        loadingEventId
        shipmentId
      }
    }
  }
`;

const AdminHome = () => {
  const { loginWithRedirect, getAccessTokenSilently } = useAuth0();

  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [shipmentId, setShipmentId] = useState<string>('');
  const [loadingEventId, setLoadingEventId] = useState<string>('');
  const [palletEventId, setPalletEventId] = useState<string>('');
  const [exceptionId, setExceptionId] = useState<string>('');
  const [taskId, setTaskId] = useState<string>('');
  const [imageId, setImageId] = useState<string>('');
  const [videoId, setVideoId] = useState<string>('');
  const [forkliftSessionId, setForkliftSessionId] = useState<string>('');
  const [llmChatId, setLlmChatId] = useState<string>('');

  const router = useRouter();

  const {
    loading_event_id: loadingEventQuery,
    pallet_event_id: palletEventQuery,
    forklift_session_id: forkliftSessionQuery,
  } = router.query;

  const [
    getLoadingEvent,
    {
      data: loadingEventData,
      loading: loadingEventLoading,
      error: loadingEventError,
    },
  ] = useLazyQuery<
    HomePageGetLoadingEventParentShipmentQuery,
    HomePageGetLoadingEventParentShipmentQueryVariables
  >(HOMEPAGE_GET_LOADING_EVENT_PARENT_SHIPMENT, {
    fetchPolicy: 'cache-and-network',
    variables: {
      loadingEventId: parseInt(loadingEventId),
    },
  });

  const [
    getForkliftSession,
    {
      data: getForkliftSessionData,
      loading: getForkliftSessionLoading,
      error: getForkliftSessionError,
    },
  ] = useLazyQuery<
    HomePageGetForkliftSessionQuery,
    HomePageGetForkliftSessionQueryVariables
  >(HOMEPAGE_GET_FORKLIFT_SESSION, {
    fetchPolicy: 'cache-and-network',
    variables: {
      forkliftSessionId: forkliftSessionId,
    },
  });

  const [
    getPalletEvent,
    {
      data: palletEventData,
      loading: palletEventLoading,
      error: palletEventError,
    },
  ] = useLazyQuery<
    HomePageGetPalletEventParentShipmentQuery,
    HomePageGetPalletEventParentShipmentQueryVariables
  >(HOMEPAGE_GET_PALLET_EVENT_PARENT_SHIPMENT, {
    fetchPolicy: 'cache-and-network',
    variables: {
      palletEventId: parseInt(palletEventId)!,
    },
  });

  const jumpToPalletEvent = useCallback(
    (palletEventId: number) => {
      getPalletEvent({
        variables: {
          palletEventId: palletEventId,
        },
      });
    },
    [getPalletEvent],
  );

  const jumpToLoadingevent = useCallback(
    (loadingEventId: number) => {
      getLoadingEvent({
        variables: {
          loadingEventId,
        },
      });
    },
    [getLoadingEvent],
  );

  const jumpToForkliftSession = useCallback(
    (forkliftSessionId: string) => {
      getForkliftSession({
        variables: {
          forkliftSessionId,
        },
      });
    },
    [getForkliftSession],
  );

  useEffect(() => {
    if (loadingEventQuery && parseInt(String(loadingEventQuery))) {
      jumpToLoadingevent(parseInt(String(loadingEventQuery)));
    }
  }, [loadingEventQuery, jumpToLoadingevent]);

  useEffect(() => {
    if (forkliftSessionQuery && parseInt(String(forkliftSessionQuery))) {
      const sessionId = String(forkliftSessionQuery);
      if (sessionId) {
        jumpToForkliftSession(sessionId);
      }
    }
  });

  useEffect(() => {
    if (palletEventQuery && parseInt(String(palletEventQuery))) {
      jumpToPalletEvent(parseInt(String(palletEventQuery)));
    }
  }, [palletEventQuery, jumpToPalletEvent]);

  const getToken = useCallback(async () => {
    try {
      const newToken = await getAccessTokenSilently();
      navigator.clipboard.writeText(`Bearer ${newToken}`);
      setSnackbarOpen(true);
    } catch (err) {
      // This is needed because package auth0-react does not have types exported for errors
      const auth0Error = err as OAuthError;

      if (auth0Error.error === 'login_required') {
        loginWithRedirect();

        return;
      }

      if (auth0Error.error === 'consent_required') {
        loginWithRedirect();

        return;
      }

      throw err;
    }
  }, [loginWithRedirect, getAccessTokenSilently]);

  useEffect(() => {
    if (loadingEventData?.ops?.loadingEvent) {
      const { shipmentId, id } = loadingEventData?.ops?.loadingEvent;
      router.push(`/shipments/${shipmentId}/loading_events/${id}`);
    }
  }, [loadingEventData?.ops?.loadingEvent, router]);

  useEffect(() => {
    if (palletEventData?.ops?.palletEvent) {
      const { id } = palletEventData?.ops?.palletEvent;

      router.push(`/labels-edit?pallet_id=${id}`);
    }
  }, [palletEventData?.ops?.palletEvent, router]);

  useEffect(() => {
    if (getForkliftSessionData?.ops.forkliftSession.id) {
      const { id } = getForkliftSessionData?.ops.forkliftSession;

      router.push(`/forklift_session/${id}`);
    }
  }, [getForkliftSessionData?.ops?.forkliftSession, router]);

  return (
    <StyledContainer>
      <Head>
        <title>Admin - Kargo</title>
      </Head>
      <Typography variant='h3'>Kargo Admin</Typography>

      <StyledLoadingAndErrorStateContainer>
        {loadingEventLoading ||
          palletEventLoading ||
          (getForkliftSessionLoading && <LinearProgress />)}
        {loadingEventError && (
          <Alert severity='error'>{loadingEventError.message}</Alert>
        )}

        {getForkliftSessionError && (
          <Alert severity='error'>{getForkliftSessionError.message}</Alert>
        )}

        {palletEventData?.ops && palletEventData.ops.palletEvent === null && (
          <Alert severity='warning'>
            Unable to find a pallet event with Id {palletEventId}
          </Alert>
        )}

        {loadingEventData?.ops &&
          loadingEventData.ops.loadingEvent === null && (
            <Alert severity='warning'>
              Unable to find a loading event with Id {loadingEventId}
            </Alert>
          )}
        {palletEventError && (
          <Alert severity='error'>{palletEventError.message}</Alert>
        )}
      </StyledLoadingAndErrorStateContainer>

      <Grid container spacing={2}>
        <Grid item xs={12} md={2}>
          <OpsTaskBacklogContainer />
        </Grid>
        <Grid item xs={12} md={10}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <ItemLookup
                placeholder='323235'
                title='Shipment'
                onSubmit={(e) => {
                  e.preventDefault();
                  router.push(`/shipments/${shipmentId}`);
                }}
                onChange={setShipmentId}
                value={shipmentId}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ItemLookup
                loading={loadingEventLoading}
                title='Loading Event'
                onSubmit={(e) => {
                  e.preventDefault();
                  getLoadingEvent({
                    variables: {
                      loadingEventId: parseInt(loadingEventId),
                    },
                  });
                }}
                placeholder='101234'
                onChange={setLoadingEventId}
                value={loadingEventId}
                disabled={loadingEventLoading}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ItemLookup
                loading={palletEventLoading}
                title='Pallet'
                placeholder='234342'
                onChange={setPalletEventId}
                onSubmit={(e) => {
                  e.preventDefault();
                  getPalletEvent({
                    variables: {
                      palletEventId: parseInt(palletEventId),
                    },
                  });
                }}
                value={palletEventId}
                disabled={palletEventLoading}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ItemLookup
                onSubmit={(e) => {
                  e.preventDefault();
                  window.open(GetMetabaseExceptionDashboardUrl(exceptionId));
                }}
                title='Exception'
                placeholder={'AX6FDCGD3434X'}
                onChange={setExceptionId}
                value={exceptionId}
                disabled={false}
                loading={false}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ItemLookup
                title='Task'
                placeholder='120345'
                loading={false}
                onSubmit={(e) => {
                  e.preventDefault();
                  window.open(GetTaskUrl(taskId));
                }}
                onChange={setTaskId}
                value={taskId}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ItemLookup
                title='Image'
                placeholder='120345'
                loading={false}
                onSubmit={(e) => {
                  e.preventDefault();
                  const externalId = ExternalIdFromUrl(imageId);
                  if (!externalId) {
                    return;
                  }
                  window.open(GetMediaUrl(externalId, 'image'));
                }}
                onChange={setImageId}
                value={imageId}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ItemLookup
                title='Video'
                placeholder='120345'
                loading={false}
                onSubmit={(e) => {
                  e.preventDefault();
                  const externalId = ExternalIdFromUrl(videoId);
                  if (!externalId) {
                    return;
                  }
                  window.open(GetMediaUrl(externalId, 'video'));
                }}
                onChange={setVideoId}
                value={videoId}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ItemLookup
                title='Forklift Session ID'
                placeholder='120345'
                loading={false}
                onSubmit={(e) => {
                  e.preventDefault();
                  getForkliftSession({
                    variables: {
                      forkliftSessionId: forkliftSessionId,
                    },
                  });
                }}
                onChange={setForkliftSessionId}
                value={forkliftSessionId}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ItemLookup
                title='LLM Chat ID'
                placeholder='120345'
                loading={false}
                onSubmit={(e) => {
                  e.preventDefault();
                  if (llmChatId) {
                    router.push(`/chat/${llmChatId}`);
                  }
                }}
                onChange={setLlmChatId}
                value={llmChatId}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Button
                startIcon={<CopyAll />}
                variant='outlined'
                className='break-words'
                onClick={getToken}
              >
                Copy Bearer Token
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert severity='success'>Token Copied</Alert>
      </Snackbar>
    </StyledContainer>
  );
};

AdminHome.getLayout = (page: JSX.Element) => (
  <AdminLayout title='Admin Home'>{page}</AdminLayout>
);

export default AdminHome;
